import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db } from '../firebase';
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  where,
  arrayUnion,
  arrayRemove,
  increment,
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import ReactMarkdown from 'react-markdown';
import styles from './DishaChat.module.css';

const DAILY_LIMIT = 30;
const MIN_INTERVAL_MS = 12000; // ~5 messages/minute
const REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

// Delete all queued (unanswered) entries for a given user.
async function clearUserQueue(uid) {
  const snap = await getDocs(query(collection(db, 'dishaQueue'), where('userId', '==', uid)));
  await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
}

export default function DishaChat() {
  const { currentUser, signInWithGoogle } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [typingVisible, setTypingVisible] = useState(false);
  const [usageCount, setUsageCount] = useState(0);
  const [error, setError] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [pickerFor, setPickerFor] = useState(null); // msg id whose picker is open
  const lastSentRef = useRef(0);
  const messagesRef = useRef(null);

  const today = useMemo(todayKey, []);
  const quotaExceeded = usageCount >= DAILY_LIMIT;
  const usageDocId = currentUser ? `${currentUser.uid}_${today}` : null;

  // Subscribe to this user's messages in real-time
  useEffect(() => {
    if (!currentUser) {
      setMessages([]);
      setLoadingHistory(false);
      return;
    }
    setLoadingHistory(true);
    const q = query(
      collection(db, 'dishaChats', currentUser.uid, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoadingHistory(false);
    }, () => setLoadingHistory(false));
    return unsub;
  }, [currentUser]);

  // Load today's usage count
  useEffect(() => {
    if (!currentUser || !usageDocId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'dishaUsage', usageDocId));
        setUsageCount(snap.exists() ? (snap.data().count || 0) : 0);
      } catch {
        // ignore
      }
    })();
  }, [currentUser, usageDocId]);

  useEffect(() => {
    // Scroll ONLY the messages box, never the whole page.
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, typingVisible]);

  // Toggle the current user's reaction (emoji) on a message.
  const toggleReaction = async (msg, emoji) => {
    if (!currentUser) return;
    const uid = currentUser.uid;
    const already = Array.isArray(msg.reactions?.[emoji]) && msg.reactions[emoji].includes(uid);
    const msgRef = doc(db, 'dishaChats', uid, 'messages', msg.id);
    try {
      await updateDoc(msgRef, {
        [`reactions.${emoji}`]: already ? arrayRemove(uid) : arrayUnion(uid),
      });
    } catch (e) {
      console.warn('reaction toggle failed', e);
    }
    setPickerFor(null);
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    if (!currentUser) {
      setError('Please sign in first.');
      return;
    }

    const now = Date.now();
    if (now - lastSentRef.current < MIN_INTERVAL_MS) {
      setError("Take a breath ❤️ — give me a few seconds to read what you wrote.");
      return;
    }
    if (usageCount >= DAILY_LIMIT) {
      // Quota-exceeded message stays visible until next day — no need to re-set.
      return;
    }

    setError('');
    lastSentRef.current = now;
    setInput('');
    setSending(true);
    setTypingVisible(false);

    // Human-pacing: stay silent for ~5–7s before showing "Disha is typing…".
    // This makes Disha feel like a real person reading & thinking, not an instant bot.
    const silentMs = 5000 + Math.floor(Math.random() * 2000);
    const typingTimer = setTimeout(() => setTypingVisible(true), silentMs);

    let userMsgRef = null;
    try {
      // 1) Save user message
      userMsgRef = await addDoc(
        collection(db, 'dishaChats', currentUser.uid, 'messages'),
        { sender: 'user', text, createdAt: serverTimestamp() }
      );
      // Update parent summary so admin thread list stays consistent.
      setDoc(doc(db, 'dishaChats', currentUser.uid), {
        userId: currentUser.uid,
        lastText: text,
        lastSender: 'user',
        lastAt: serverTimestamp(),
      }, { merge: true }).catch(e => console.warn('summary write failed', e));

      // 2) Build conversation history (exclude the message just sent)
      const history = messages
        .slice(-10)
        .map(m => ({
          role: m.sender === 'user' ? 'user' : 'assistant',
          content: m.text || '',
        }));

      // 3) Ask the mentor endpoint (with one retry so a single transient
      //    failure — e.g. a provider hiccup or a cold-start — doesn't leave
      //    Disha silent).
      const callMentor = async () => {
        const r = await fetch('/api/disha-mentor', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question: text, history }),
        });
        const t = await r.text();
        let d = {};
        try { d = JSON.parse(t); } catch { /* non-JSON */ }
        return { resp: r, data: d, rawText: t };
      };
      const apiStarted = Date.now();
      let { resp, data, rawText } = await callMentor();
      if (!resp.ok || !data?.answer) {
        // brief pause then retry once
        await new Promise(r => setTimeout(r, 1200));
        try {
          const retry = await callMentor();
          if (retry.resp.ok && retry.data?.answer) {
            resp = retry.resp; data = retry.data; rawText = retry.rawText;
          }
        } catch (_) { /* swallow, original failure stands */ }
      }
      const apiElapsed = Date.now() - apiStarted;

      // Log details so we can see exactly what went wrong if Disha is silent.
      if (!resp.ok || !data?.answer) {
        console.warn('[Disha] API responded without a usable answer', {
          status: resp.status,
          ok: resp.ok,
          data,
          rawTextPreview: rawText.slice(0, 300),
        });
      }

      // Ensure we have stayed silent at least `silentMs` total.
      if (apiElapsed < silentMs) {
        await new Promise(r => setTimeout(r, silentMs - apiElapsed));
      }
      setTypingVisible(true);

      // Now "type" for a short window proportional to reply length.
      const replyLen = (data?.answer || '').length;
      const typeMs = Math.min(9000, 2500 + replyLen * 25);
      await new Promise(r => setTimeout(r, typeMs));

      if (data?.answer) {
        await addDoc(
          collection(db, 'dishaChats', currentUser.uid, 'messages'),
          {
            sender: 'disha',
            text: data.answer,
            provider: data.provider || 'unknown',
            createdAt: serverTimestamp(),
          }
        );
        setDoc(doc(db, 'dishaChats', currentUser.uid), {
          lastText: data.answer,
          lastSender: 'disha',
          lastAt: serverTimestamp(),
        }, { merge: true }).catch(e => console.warn('summary write failed', e));
        // Resolve any previously queued (unanswered) messages for this user.
        clearUserQueue(currentUser.uid).catch(e => console.warn('queue clear failed', e));
      } else {
        // No answer right now — silently queue for follow-up.
        // We deliberately do NOT show any user-facing error: Disha should always
        // feel like a real human mentor who is just taking a little longer.
        await addDoc(collection(db, 'dishaQueue'), {
          userId: currentUser.uid,
          userMsgId: userMsgRef?.id || null,
          question: text,
          history,
          createdAt: serverTimestamp(),
          processed: false,
        });
      }

      // 4) Increment daily usage
      await setDoc(
        doc(db, 'dishaUsage', usageDocId),
        {
          count: increment(1),
          userId: currentUser.uid,
          date: today,
          lastAt: serverTimestamp(),
        },
        { merge: true }
      );
      setUsageCount(c => c + 1);
    } catch (err) {
      console.error('Disha send error:', err);
      // Stay silent on errors — Disha must always feel human, never "broken".
      // The user's message is already saved; admin can follow up.
    } finally {
      clearTimeout(typingTimer);
      setTypingVisible(false);
      setSending(false);
    }
  };

  if (!currentUser) {
    return (
      <div className={styles.signinPrompt}>
        <h3>Sign in first to Chat.</h3>
        <p>Your space is 100% private. Conversation is private between you and us.</p>
        <button className={styles.signinBtn} onClick={() => signInWithGoogle()}>
          Sign in with Google
        </button>
      </div>
    );
  }

  return (
    <div className={styles.chatContainer}>
      <div className={styles.chatHeader}>
        <div className={styles.avatar}>D</div>
        <div className={styles.headerText}>
          <div className={styles.name}>Chat with Disha</div>
          <div className={styles.subtitle}>Your Personal Mentor ・ 24*7 ❤️</div>
        </div>
        <div className={styles.usageBadge} title="Messages today">
          {usageCount}/{DAILY_LIMIT} (daily {DAILY_LIMIT}Q limit)
        </div>
      </div>

      <div className={styles.messages} ref={messagesRef}>
        {loadingHistory ? (
          <div className={styles.welcome}>Loading our conversation…</div>
        ) : messages.length === 0 ? (
          <div className={styles.welcome}>
            <p><strong>Hi, I'm Disha 🤗</strong></p>
            <p>
              This is your safe space. Whatever you're feeling — stress, pressure,
              loneliness, confusion about life — I'm here to listen.
              Just type whatever's on your heart.
            </p>
          </div>
        ) : (
          messages.map(m => {
            const isUser = m.sender === 'user';
            const senderName = isUser
              ? (currentUser?.displayName || currentUser?.email?.split('@')[0] || 'You')
              : 'Disha';
            const when = m.createdAt?.toDate ? m.createdAt.toDate().toLocaleString() : '';
            const reactions = m.reactions || {};
            const myUid = currentUser?.uid;
            const reactionEntries = Object.entries(reactions)
              .filter(([, uids]) => Array.isArray(uids) && uids.length > 0);
            return (
              <div
                key={m.id}
                className={`${styles.messageRow} ${isUser ? styles.rowUser : styles.rowDisha}`}
              >
                <div className={styles.bubbleWrap}>
                  <div
                    className={`${styles.message} ${isUser ? styles.user : styles.disha}`}
                  >
                    {isUser ? m.text : <ReactMarkdown>{m.text}</ReactMarkdown>}
                  </div>
                  <button
                    type="button"
                    className={styles.reactBtn}
                    onClick={() => setPickerFor(pickerFor === m.id ? null : m.id)}
                    aria-label="Add reaction"
                    title="Add reaction"
                  >
                    ☺
                  </button>
                  {pickerFor === m.id && (
                    <div className={styles.reactPicker} role="toolbar">
                      {REACTIONS.map(e => (
                        <button
                          key={e}
                          type="button"
                          className={styles.reactPickerBtn}
                          onClick={() => toggleReaction(m, e)}
                          aria-label={`React with ${e}`}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {reactionEntries.length > 0 && (
                  <div className={styles.reactionChips}>
                    {reactionEntries.map(([emoji, uids]) => {
                      const mine = uids.includes(myUid);
                      return (
                        <button
                          key={emoji}
                          type="button"
                          className={`${styles.reactionChip} ${mine ? styles.reactionChipMine : ''}`}
                          onClick={() => toggleReaction(m, emoji)}
                          title={mine ? 'Remove your reaction' : 'Add your reaction'}
                        >
                          <span>{emoji}</span>
                          <span className={styles.reactionCount}>{uids.length}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className={styles.msgMeta}>
                  {senderName}
                  {when ? ` \u00b7 ${when}` : ''}
                </div>
              </div>
            );
          })
        )}
        {sending && typingVisible && (
          <div className={`${styles.messageRow} ${styles.rowDisha}`}>
            <div className={`${styles.message} ${styles.disha} ${styles.typing}`}>
              Disha is typing<span className={styles.dots}>…</span>
            </div>
          </div>
        )}
      </div>

      {(quotaExceeded || error) && (
        <div className={`${styles.errorBar} ${quotaExceeded ? styles.quotaBar : ''}`}>
          {quotaExceeded
            ? `Aaj ke liye daily limit (${DAILY_LIMIT} messages) complete ho gayi hai. Kal phir baat karte hain — main yahin hoon.`
            : error}
        </div>
      )}

      <form className={styles.inputBar} onSubmit={handleSend}>
        <textarea
          rows={2}
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={quotaExceeded ? 'Daily limit reached — come back tomorrow' : "Share what's on your mind…"}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend(e);
            }
          }}
          disabled={sending || quotaExceeded}
          maxLength={4000}
        />
        <button
          type="submit"
          disabled={sending || !input.trim() || quotaExceeded}
          title={quotaExceeded ? 'Daily quota exceeded — try again tomorrow' : 'Send'}
        >
          {quotaExceeded ? '👁' : 'Send'}
        </button>
      </form>
    </div>
  );
}
